import {
  Button,
  Grid,
  Text,
  Card,
  Toggle,
  Divider,
  useTheme,
} from "@geist-ui/core";
import { Info, AlertCircle, ChevronRight, Check } from "@geist-ui/icons";
import { sha256 } from "js-sha256";
import React, { useState, useContext, useEffect } from "react";
import { motion } from "framer-motion";
import TagManager from "react-gtm-module";


import {
  SecondaryButton,
} from "../../../../components/input-elements/buttons";
import LoadingScreen from "../../../../components/loading-screen";
import { MultistepFormButtonsV2 } from "../../../../components/vertical-multistep-form/multistep-form-buttons";
import { MatchingDTO } from "../../../../types/generic_types";
import {
  EXPERIENCE,
  KNOWLEDGE,
  SERVICE_KNOWLEDGE_OR_EXPERIENCE,
  INVESTMENT_COUNT,
  INVESTMENT_SIZE,
  WAIVER_TYPE,
  Investment,
  Survey,
} from "../../../../types/investment_types";
import API_CLIENT from "../../../../utility/api-client";
import { InvestmentContextAIF } from "../../../portal/investment-aif/investment-process-aif";
import { AppContext } from "../../direct-router";
import { InvestmentContextFull } from "../investment-process-full";

type InvestmentSurveyAIFProps = {
  back: () => void;
  next: () => void;
};

// --- STYLES COPIED FOR CONSISTENCY ---
const snappyTransition = 'all 0.4s cubic-bezier(0.25, 1, 0.5, 1)';

const inputBaseStyle: React.CSSProperties = {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    border: '1px solid #D4D4D4',
    height: '56px',
    fontSize: '16px',
    color: '#000',
    width: '100%',
    padding: '0 20px',
    paddingRight: '40px', // For chevron
    transition: snappyTransition,
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
    transform: 'scale(1)',
    outline: 'none',
    fontFamily: 'inherit',
    appearance: 'none',
    cursor: 'pointer'
};

const inputFocusStyle: React.CSSProperties = {
    borderColor: '#000',
    borderWidth: '1px',
    backgroundColor: '#fff',
    boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
    transform: 'scale(1.005) translateY(-2px)',
};

const getFocusHandlers = (theme: any, error?: boolean) => ({
    onFocus: (e: React.FocusEvent<HTMLSelectElement>) => {
        Object.assign(e.currentTarget.style, inputFocusStyle);
    },
    onBlur: (e: React.FocusEvent<HTMLSelectElement>) => {
        e.currentTarget.style.borderColor = error ? theme.palette.error : '#D4D4D4';
        e.currentTarget.style.borderWidth = '1px';
        e.currentTarget.style.backgroundColor = '#FFFFFF';
        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02)';
        e.currentTarget.style.transform = 'scale(1) translateY(0)';
    }
});

const CustomSelect = ({ 
    value, 
    onChange, 
    options, 
    placeholder, 
    width = "100%" 
}: { 
    value: string | undefined, 
    onChange: (val: string) => void, 
    options: { value: string, label: string }[], 
    placeholder?: string,
    width?: string 
}) => {
    const theme = useTheme();
    return (
        <div style={{ position: 'relative', width }}>
            <select
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                style={inputBaseStyle}
                className="w-full"
                {...getFocusHandlers(theme)}
            >
                 <option value="" disabled hidden>{placeholder || "Bitte wählen..."}</option>
                 {options.map(opt => (
                     <option key={opt.value} value={opt.value}>{opt.label}</option>
                 ))}
            </select>
            {/* Custom Chevron */}
            <div style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1.5L6 6.5L11 1.5" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </div>
        </div>
    )
}


export function SurveyFull({ back, next }: InvestmentSurveyAIFProps) {
  //Context
  const app_context = useContext(AppContext);
  const investment_context = useContext(InvestmentContextFull);
  const fund = investment_context.fund;
  const campaign = investment_context.campaign;
  const investment = investment_context.investment;

  // Standardized Table Card Style
  const surveyTableCardStyle = {
      borderRadius: '16px',
      border: '1px solid #EAEAEA',
      boxShadow: 'none',
      padding: 0,
      overflow: "hidden",
      width: "100%",
      backgroundColor: '#FFFFFF',
      marginBottom: '1.5rem'
  };

  // Standardized Section Card Style
  const sectionCardStyle = {
      borderRadius: '16px',
      border: '1px solid #EAEAEA',
      boxShadow: 'none',
      padding: 'clamp(16px, 3vw, 24px)',
      marginBottom: '1.5rem',
      backgroundColor: '#FFFFFF',
      width: '100%'
  };

  const no_answer_survey: Survey = {
    investmentexperience_investment_fund: EXPERIENCE.NO_ANSWER,
    investmentknowledge_investment_fund: KNOWLEDGE.NO_ANSWER,
    investmentexperience_money_market_funds: EXPERIENCE.NO_ANSWER,
    investmentknowledge_money_market_funds: KNOWLEDGE.NO_ANSWER,

    investmentexperience_bond: EXPERIENCE.NO_ANSWER,
    investmentknowledge_bond: KNOWLEDGE.NO_ANSWER,
    investmentexperience_share: EXPERIENCE.NO_ANSWER,
    investmentknowledge_share: KNOWLEDGE.NO_ANSWER,

    investmentexperience_participation_right: EXPERIENCE.NO_ANSWER,
    investmentknowledge_participation_right: KNOWLEDGE.NO_ANSWER,
    investmentexperience_unsecured_junior_debt: EXPERIENCE.NO_ANSWER,
    investmentknowledge_unsecured_junior_debt: KNOWLEDGE.NO_ANSWER,
    investmentexperience_crypto_currency: EXPERIENCE.NO_ANSWER,
    investmentknowledge_crypto_currency: KNOWLEDGE.NO_ANSWER,
    investmentexperience_pension_funds: EXPERIENCE.NO_ANSWER,
    investmentknowledge_pension_funds: KNOWLEDGE.NO_ANSWER,
    investmentexperience_fund_of_funds: EXPERIENCE.NO_ANSWER,
    investmentknowledge_fund_of_funds: KNOWLEDGE.NO_ANSWER,
    investmentexperience_stock_funds: EXPERIENCE.NO_ANSWER,
    investmentknowledge_stock_funds: KNOWLEDGE.NO_ANSWER,
    investmentexperience_participation: EXPERIENCE.NO_ANSWER,
    investmentknowledge_participation: KNOWLEDGE.NO_ANSWER,

    serviceexperience_independent: SERVICE_KNOWLEDGE_OR_EXPERIENCE.NO_ANSWER,
    serviceexperience_investment_consultancy:
      SERVICE_KNOWLEDGE_OR_EXPERIENCE.NO_ANSWER,
    serviceexperience_investment_management:
      SERVICE_KNOWLEDGE_OR_EXPERIENCE.NO_ANSWER,
    serviceexperience_investment_brokerage:
      SERVICE_KNOWLEDGE_OR_EXPERIENCE.NO_ANSWER,

    avgyearlytransactions: INVESTMENT_COUNT.NO_ANSWER,
    avgtransactionvalue: INVESTMENT_SIZE.NO_ANSWER,

    scoreKnowledgeExperience: 0,
    scoreTransactions: 0,
    scoreService: 0,
  };

  const empty_survey: Survey = {
    investmentexperience_investment_fund: EXPERIENCE.NO_ANSWER,
    investmentknowledge_investment_fund: KNOWLEDGE.NO_KNOWLEDGE,
    investmentexperience_money_market_funds: EXPERIENCE.NO_ANSWER,
    investmentknowledge_money_market_funds: KNOWLEDGE.NO_KNOWLEDGE,

    investmentexperience_bond: EXPERIENCE.NO_ANSWER,
    investmentknowledge_bond: KNOWLEDGE.NO_KNOWLEDGE,
    investmentexperience_share: EXPERIENCE.NO_ANSWER,
    investmentknowledge_share: KNOWLEDGE.NO_KNOWLEDGE,

    investmentexperience_participation_right: EXPERIENCE.NO_ANSWER,
    investmentknowledge_participation_right: KNOWLEDGE.NO_KNOWLEDGE,
    investmentexperience_unsecured_junior_debt: EXPERIENCE.NO_ANSWER,
    investmentknowledge_unsecured_junior_debt: KNOWLEDGE.NO_KNOWLEDGE,
    investmentexperience_crypto_currency: EXPERIENCE.NO_ANSWER,
    investmentknowledge_crypto_currency: KNOWLEDGE.NO_KNOWLEDGE,
    investmentexperience_pension_funds: EXPERIENCE.NO_ANSWER,
    investmentknowledge_pension_funds: KNOWLEDGE.NO_ANSWER,
    investmentexperience_fund_of_funds: EXPERIENCE.NO_ANSWER,
    investmentknowledge_fund_of_funds: KNOWLEDGE.NO_ANSWER,
    investmentexperience_stock_funds: EXPERIENCE.NO_ANSWER,
    investmentknowledge_stock_funds: KNOWLEDGE.NO_ANSWER,
    investmentexperience_participation: EXPERIENCE.NO_ANSWER,
    investmentknowledge_participation: KNOWLEDGE.NO_ANSWER,

    serviceexperience_independent:
      SERVICE_KNOWLEDGE_OR_EXPERIENCE.NO_KNOWLEDGE_OR_EXPERIENCE,
    serviceexperience_investment_consultancy:
      SERVICE_KNOWLEDGE_OR_EXPERIENCE.NO_KNOWLEDGE_OR_EXPERIENCE,
    serviceexperience_investment_management:
      SERVICE_KNOWLEDGE_OR_EXPERIENCE.NO_KNOWLEDGE_OR_EXPERIENCE,
    serviceexperience_investment_brokerage:
      SERVICE_KNOWLEDGE_OR_EXPERIENCE.NO_KNOWLEDGE_OR_EXPERIENCE,

    avgyearlytransactions: INVESTMENT_COUNT.NO_TRANS,
    avgtransactionvalue: INVESTMENT_SIZE.LOWER_3000,

    scoreKnowledgeExperience: 0,
    scoreTransactions: 0,
    scoreService: 0,
  };

  const [unsuitability_checkbox, setUnsuitabilityCheckbox] = useState(false);
  const [no_answer, setNoAnswer] = useState(false);
  const [survey_update, setSurveyUpdate] = useState(false);
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [use_previous, setUsePrevious] = useState(false);
  const [loading_next_step, setLoadingNextStep] = useState(false);
  const [last_survey_done, setLastSurveyDone] = useState(false);
  const [survey_done, setSurveyDone] = useState(
    investment_context.investment?.survey != null
  );
  const [active_step, setActiveStep] = useState(0);

  useEffect(() => {
    console.log(investment_context.last_investment);
    const investment = investment_context.investment;
    window.scrollTo(0, 0);
    const survey_investment_score =
      investment?.survey?.scoreKnowledgeExperience;
    const survey_service_score = investment?.survey?.scoreService;
    const survey_transaction_score = investment?.survey?.scoreTransactions;

    const campaign_investment_score = campaign?.experienceAndKnowledgeScore;
    const campaign_service_score = campaign?.serviceScore;
    const campaign_transaction_score = campaign?.amountAndVolumeScore;

    if (
      survey_investment_score &&
      survey_service_score &&
      survey_transaction_score &&
      campaign_investment_score &&
      campaign_service_score &&
      campaign_transaction_score
    ) {
      // Logic for validation here if needed in future
    }
    if (investment_context.investment?.survey == null) {
      setSurvey(empty_survey);
    } else {
      setSurvey(investment_context.investment?.survey);
    }
  }, []);


  if (investment == null || campaign == null) {
    return <LoadingScreen />;
  }

  //API Calls
  const track = () => {
    var uData = app_context.matchingData;
    var mData: MatchingDTO = {
      event: "Wallet",
      eventId: investment.id,
      sourceUrl: window.location.href,
      em: uData?.em,
      ph: uData?.ph,
      fn: uData?.fn,
      ln: uData?.ln,
      product: fund?.name,
      focus: fund?.fokus,
      type: fund?.type,
      value: investment.amount.toString(),
      currency: "EUR",
    };
    API_CLIENT.postMatching(app_context.token, mData);

    TagManager.dataLayer({
      dataLayer: {
        event: "view-investment-process-wallet",
        event_id: investment.id,
        first_name: app_context.user?.natural_person.forename,
        last_name: app_context.user?.natural_person.surname,
        phone: uData?.ph,
        email: sha256(app_context.userEmail),
        product: fund?.name,
        focus: fund?.fokus,
        type: fund?.type,
        value: investment.amount,
        currency: "EUR",
      },
    });
  };

  const sendSurvey = (noAnswer?: boolean) => {
    console.log("send survey");
    window.scrollTo(0, 0);
    setLoadingNextStep(true);
    if (survey == null) return;
    const surveyToSend = noAnswer ? no_answer_survey : survey;
    API_CLIENT.putInvestmentSurvey(
      app_context.token,
      investment.id,
      surveyToSend,
      () => {
        console.log("survey score too low");
        investment.survey_waiver = null;
        investment_context.setInvestment(investment);
        setActiveStep(2);
        setLoadingNextStep(false);
      },
      () => {
        console.log("survey has empty fields");
        investment.survey_waiver = null;
        investment_context.setInvestment(investment);
        setActiveStep(1);
        setLoadingNextStep(false);
      },
      (investment) => {
        console.log("survey valid: updated investment");
        track();
        investment_context.setInvestment(investment);
        next();
      },
      (error) => {
        setLoadingNextStep(false);
      }
    );
  };

  const sendNoAnswerWaiver = () => {
    console.log("sending no answer waiver");
    investment_context.setLoading(true);
    window.scrollTo(0, 0);

    setLoadingNextStep(true);
    API_CLIENT.putSurveyWaiver(
      app_context.token,
      investment.id,
      WAIVER_TYPE.NO_ANSWERS,
      (investment: Investment) => {
        track();
        investment_context.setInvestment(investment);
        investment_context.setLoading(false);
      },
      (error: string) => {}
    );
  };

  const UseLastInvestmentDialogue = () => {
    if (investment_context.last_investment == null) return null;
    return (
      <Card style={sectionCardStyle}>
        <div className="flex flex-col gap-4">
          <Text h3 style={{ margin: 0, fontSize: "18px" }}>
            Bitte tragen Sie hier Ihre Kenntnisse und Erfahrungen ein.
          </Text>
          <Text p style={{ fontSize: "14px", color: "#666", margin: 0 }}>
            Bei Ihrer letzten Investition haben Sie bereits Ihre Kenntnisse und
            Erfahrungen angegeben. Möchten Sie diese übernehmen?
          </Text>
          <div className="flex flex-col sm:flex-row gap-4 pt-4">
            <motion.button
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 px-8 py-4 rounded-full bg-black text-white font-extrabold text-sm uppercase tracking-tight shadow-xl transition-all"
                onClick={() => {
                if (investment_context.last_investment) {
                  setSurvey(investment_context.last_investment.survey);
                }
                setLastSurveyDone(true);
              }}
            >
              Ja, Angaben übernehmen
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-8 py-4 rounded-full border border-black bg-white text-black font-bold text-sm uppercase tracking-tight transition-all"
              onClick={() => {
                setSurvey(empty_survey);
                setLastSurveyDone(true);
              }}
            >
              Erneut ausfüllen
            </motion.button>
          </div>
        </div>
      </Card>
    );
  };

  const sendUnsuitabilityWaiver = () => {
    console.log("sending unsuitability waiver");
    window.scrollTo(0, 0);

    investment_context.setLoading(true);
    API_CLIENT.putSurveyWaiver(
      app_context.token,
      investment.id,
      WAIVER_TYPE.UNSUITABLE,
      (investment: Investment) => {
        investment_context.setInvestment(investment);
        track();
        investment_context.setLoading(false);
      },
      (error: string) => {
        console.log(error);
      }
    );
  };

  //Components
  const NoAnswerWaiver = () => {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          sendNoAnswerWaiver();
        }}
      >
        <div className="grid gap-8">
            <Text h3>
              Bitte tragen Sie hier Ihre Kenntnisse und Erfahrungen ein.
            </Text>
            <div>
              <Text h4>Kenntnissnahme</Text>
              <div style={{ 
                  borderRadius: "6px", 
                  border: "1px solid #ff0000", 
                  backgroundColor: "#fff5f5", 
                  padding: "16px",
                  color: "#333",
                  fontSize: "14px",
                  lineHeight: "1.6"
              }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: "12px", color: "#e00" }}>
                   <AlertCircle size={20} />
                   <Text b style={{ marginLeft: "8px", fontSize: "16px", color: "inherit", margin: "0 0 0 8px" }}>Hinweis</Text>
                </div>
                <p style={{ margin: 0 }}>
                  Die Angemessenheit beurteilt sich danach, ob Sie als Anleger
                  über die erforderlichen Kenntnisse und Erfahrungen verfügen,
                  um die Risiken im Zusammenhang mit der Art der vorliegenden
                  Kapitalanlage oder Finanzdienstleistung angemessen beurteilen
                  zu können.
                  <br />
                  <br />
                  In den von Ihnen zurückgesendeten Unterlagen haben Sie
                  <b> unvollständige oder im Wesentlichen keine Angaben</b> zu
                  Ihren bisherigen Erfahrungen und Kenntnissen im Zusammenhang
                  mit Kapitalanlagen und Finanzdienstleistungen gemacht. Dadurch
                  sind wir nicht in der Lage, unserer Pflicht der Prüfung der
                  Angemessenheit der Anlage nachzukommen.
                  <br />
                  <br />
                  Wir weisen Sie daher eindringlich darauf hin, dass eine
                  Angemessenheitsprüfung bei fehlenden/unvollständigen Angaben
                  nicht möglich ist. Sie müssen daher selbst entscheiden, ob die
                  hier vorliegende Kapitalanlage für Sie angemessen ist.
                  Insbesondere sollten Sie sich mit den Risiken und
                  Gegebenheiten der vorliegenden Kapitalanlage vertraut machen.
                  Eine umfassende Darstellung der mit dieser Beteiligung
                  verbundenen Risiken enthält der Verkaufsprospekt im Abschnitt
                  „V. Wesentliche Risiken” auf den Seiten 16 bis 34.
                  <br />
                  <br />
                  Sollten Sie dennoch von einer Zeichnung Abstand nehmen wollen,
                  können Sie Ihr 14-tägiges Widerrufsrecht geltend machen.
                </p>
              </div>
            </div>
          <div>
            <MultistepFormButtonsV2
              nextButtonText="Bestätigen"
              disabled={false}
              back
              backFunction={() => setActiveStep(0)}
            />
          </div>
        </div>
      </form>
    );
  };

  const UnsuitabilityWaiver = () => {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (unsuitability_checkbox) sendUnsuitabilityWaiver();
        }}
      >
        <div className="grid gap-8">
          <Text h3>
            Bitte tragen Sie hier Ihre Kenntnisse und Erfahrungen ein.
          </Text>
          <div>
            <div>
              <Text h4>Kenntnisnahme</Text>
              <Text p>
                Die Auswertung der von Ihnen gemachten Angaben zu bisherigen
                Kenntnissen und Erfahrungen relevanter Kapitalmarktprodukte
                ergab, dass das hier angebotene Produkt bzw. die Höhe Ihrer
                Zeichnung nicht dem auf Basis Ihrer Angaben erstellten
                Anlageprofil entspricht. Gemäß Ihrer Angaben verfügen Sie über
                nicht ausreichende Kenntnisse und Erfahrungen, um die mit dem
                Erwerb des hier angebotenen Produkts verbundenen Risiken
                ausreichend beurteilen zu können, d.h. Ihre Zeichnung entspricht
                nicht Ihrem bisherigen Anlageprofil.
              </Text>
              <div style={{ 
                  borderRadius: "6px", 
                  border: "1px solid #ff0000", 
                  backgroundColor: "#fff5f5", 
                  padding: "16px",
                  color: "#333",
                  fontSize: "14px",
                  lineHeight: "1.6"
              }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: "12px", color: "#e00" }}>
                   <AlertCircle size={20} />
                   <Text b style={{ marginLeft: "8px", fontSize: "16px", color: "inherit", margin: "0 0 0 8px" }}>Wichtig</Text>
                </div>
                <p style={{ margin: 0 }}>
                  Bitte informieren Sie sich noch einmal über die mit der
                  Kapitalanlage einhergehenden Risiken. Eine umfassende
                  Darstellung der mit dieser Beteiligung verbundenen Risiken
                  enthält der Verkaufsprospekt im Abschnitt „V. Wesentliche
                  Risiken” auf den Seiten 16 bis 34.
                </p>
              </div>
            </div>
            <Text p b style={{ textAlign: "justify" }}>
              Wenn Sie sich entscheiden, das Produkt dennoch zu zeichnen, bitten
              wir Sie, folgendes zu bestätigen:
            </Text>
          </div>
          <Grid.Container
            gap={2}
            alignItems="center"
            style={{ marginBottom: "50px" }}
          >
            <Grid xs={24} md={4} alignItems="center">
                {/* Fixed height container for centered scaled Toggle */}
                <div style={{ height: '32px', display: 'flex', alignItems: 'center' }}>
                    <Toggle
                      scale={1.6}
                      checked={unsuitability_checkbox}
                      onChange={(e) => {
                        setUnsuitabilityCheckbox(e.target.checked);
                      }}
                    />
                </div>
            </Grid>
            <Grid xs={24} md={20}>
              <Text>
                <b>
                Ich bin darüber informiert, dass aufgrund der von mir
                angegebenen bisherigen Kenntnisse und Erfahrungen relevanter
                Kapitalmarktprodukte die Zeichnung der Kapitalanlage nicht
                meinem Anlageprofil entspricht.
                </b>
              </Text>
            </Grid>
          </Grid.Container>
          <Grid.Container>
             <Grid xs={12}>
                <MultistepFormButtonsV2
                  nextButtonText="Bestätigen"
                  disabled={!unsuitability_checkbox}
                  back
                  backFunction={() => setActiveStep(0)}
                />
             </Grid>
          </Grid.Container>
        </div>
      </form>
    );
  };

  const Survey = () => {
    if (!survey) return null;
    return (
      <div className="flex flex-col gap-8 w-full">
        {use_previous ? (
          <div className="flex flex-col gap-6">
            <Text h3 style={{ margin: 0 }}>
              Einschätzung Ihrer Erfahrungen als investierende Person
            </Text>
            <Text p>
              Sie haben bereits Angaben für dieses Investment gemacht. Möchten
              Sie diese beibehalten?
            </Text>
            <div className="flex flex-col sm:flex-row gap-4 mt-4">
              <motion.button
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 px-8 py-4 rounded-full bg-black text-white font-extrabold text-sm uppercase tracking-tight shadow-xl transition-all"
                onClick={() => {
                  //onSkip(investment);
                }}
              >
                Überspringen
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 px-8 py-4 rounded-full border border-black bg-white text-black font-bold text-sm uppercase tracking-tight transition-all"
                onClick={() => {
                  setUsePrevious(false);
                }}
              >
                Angaben Überprüfen
              </motion.button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-8 w-full">
            <div>
              <Text p>
                Wir sind gesetzlich dazu verpflichtet, Ihre Kenntnisse und
                Erfahrungen bezüglich verschiedener Produktklassen wie
                Wertpapieren, Vermögensanlagen und sonstigen
                Kapitalmarktprodukten abzufragen.
                <br />
                <br />
                Mittels der Antworten können wir für Sie prüfen, ob die
                Zeichnung dieser Kapitalanlage Ihren Kenntnissen und Erfahrungen
                aus Ihrem bisherigen Anlageverhalten entspricht. Die
                Beantwortung dieser Fragen ist jedoch freiwillig. Machen Sie
                keine oder unvollständige Angaben, können wir die Angemessenheit
                des jeweiligen Kapitalmarktprodukts jedoch nicht für Sie prüfen.
                Daher sollten Sie aus eigenem Interesse Angaben machen.
              </Text>
            </div>

            <div className="flex flex-col gap-2 mb-8">
              <Card style={{ 
                  borderRadius: '16px',
                  border: '1px solid #EAEAEA',
                  boxShadow: 'none',
                  padding: 'clamp(16px, 3vw, 24px)',
                  backgroundColor: '#FFFFFF',
                  width: '100%'
              }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Text b style={{ margin: 0, fontSize: "15px" }}>
                      Ich möchte keine Angaben machen
                    </Text>
                  </div>
                  <div style={{ width: "fit-content", height: '32px', display: 'flex', alignItems: 'center' }}>
                    <Toggle
                      scale={1.6}
                      initialChecked={no_answer}
                      onChange={(e) => {
                        window.scrollTo(0, 0);
                        setNoAnswer(e.target.checked);
                      }}
                    />
                  </div>
                </div>
              </Card>
              <Text small type="secondary" style={{ paddingLeft: "4px" }}>
                Wenn Sie diese Option wählen, überspringen Sie die detaillierte
                Abfrage.
              </Text>
            </div>

            <div className="w-full">
              {no_answer ? (
                <form
                  className="mt-8"
                  onSubmit={(e) => {
                    e.preventDefault();
                    sendSurvey(true);
                  }}
                >
                  <MultistepFormButtonsV2
                    nextButtonText="Bestätigen"
                    disabled={false}
                    back={false}
                    backFunction={() => null}
                  />
                </form>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    console.log(investment);
                    sendSurvey();
                  }}
                  className="w-full"
                >
                  <div className="flex flex-col gap-12">
                    {/* SECTION 1: PRODUCTS */}
                    <div className="flex flex-col gap-4">
                      <Text h4 style={{ margin: 0 }}>
                        Erfahrungen mit Kapitalmarktprodukten
                      </Text>

                      <Card
                        style={surveyTableCardStyle}
                      >
                        {/* Header - Desktop Only - CLEANER STYLE */}
                        <div className="hidden md:flex bg-white px-6 py-4 items-center border-b border-gray-100">
                          <div className="w-1/3 text-xs font-bold uppercase tracking-wider text-black">
                            Produktklasse
                          </div>
                          <div className="w-1/3 text-xs font-bold uppercase tracking-wider text-black">
                            Kenntnisse
                          </div>
                          <div className="w-1/3 text-xs font-bold uppercase tracking-wider text-black">
                            Erfahrungen
                          </div>
                        </div>

                        <div className="flex flex-col divide-y divide-gray-100">
                              <ProductRow
                                defaultValueKnowledge={
                                  survey.investmentknowledge_pension_funds
                                }
                                defaultValueExperience={
                                  survey.investmentexperience_pension_funds
                                }
                            question="Rentenfonds"
                            onChangeKnowledge={(newvalue) => {
                              survey.investmentknowledge_pension_funds =
                                newvalue;
                              setSurvey(survey);
                              setSurveyUpdate(!survey_update);
                            }}
                                onChangeExperience={(newvalue) => {
                                  survey.investmentexperience_pension_funds =
                                    newvalue;
                                  setSurvey(survey);
                                  setSurveyUpdate(!survey_update);
                                }}
                              />
                              <ProductRow
                                defaultValueKnowledge={
                                  survey.investmentknowledge_bond
                                }
                            defaultValueExperience={
                              survey.investmentexperience_bond
                            }
                            question="Anleihen"
                            onChangeExperience={(newvalue) => {
                              survey.investmentexperience_bond = newvalue;
                              setSurvey(survey);
                              setSurveyUpdate(!survey_update);
                            }}
                                onChangeKnowledge={(newvalue) => {
                                  survey.investmentknowledge_bond = newvalue;
                                  setSurvey(survey);
                                  setSurveyUpdate(!survey_update);
                                }}
                              />
                              <ProductRow
                                defaultValueKnowledge={
                                  survey.investmentknowledge_share
                                }
                            defaultValueExperience={
                              survey.investmentexperience_share
                            }
                            question="Aktien"
                            onChangeKnowledge={(newvalue) => {
                              survey.investmentknowledge_share = newvalue;
                              setSurvey(survey);
                              setSurveyUpdate(!survey_update);
                            }}
                                onChangeExperience={(newvalue) => {
                                  survey.investmentexperience_share = newvalue;
                                  setSurvey(survey);
                                  setSurveyUpdate(!survey_update);
                                }}
                              />
                              <ProductRow
                                defaultValueKnowledge={
                                  survey.investmentknowledge_money_market_funds
                                }
                            defaultValueExperience={
                              survey.investmentexperience_money_market_funds
                            }
                            question="Geldmarkt(nahe)fonds"
                            onChangeKnowledge={(newvalue) => {
                              survey.investmentknowledge_money_market_funds =
                                newvalue;
                              setSurvey(survey);
                              setSurveyUpdate(!survey_update);
                            }}
                                onChangeExperience={(newvalue) => {
                                  survey.investmentexperience_money_market_funds =
                                    newvalue;
                                  setSurvey(survey);
                                  setSurveyUpdate(!survey_update);
                                }}
                              />
                              <ProductRow
                                defaultValueKnowledge={
                                  survey.investmentknowledge_participation
                                }
                            defaultValueExperience={
                              survey.investmentexperience_participation
                            }
                            question="Beteiligungen"
                            onChangeKnowledge={(newvalue) => {
                              survey.investmentknowledge_participation =
                                newvalue;
                              setSurvey(survey);
                              setSurveyUpdate(!survey_update);
                            }}
                                onChangeExperience={(newvalue) => {
                                  survey.investmentexperience_participation =
                                    newvalue;
                                  setSurvey(survey);
                                  setSurveyUpdate(!survey_update);
                                }}
                              />
                              <ProductRow
                                defaultValueKnowledge={
                                  survey.investmentknowledge_stock_funds
                                }
                            defaultValueExperience={
                              survey.investmentexperience_stock_funds
                            }
                            question="Aktienfonds"
                            onChangeKnowledge={(newvalue) => {
                              survey.investmentknowledge_stock_funds = newvalue;
                              setSurvey(survey);
                              setSurveyUpdate(!survey_update);
                            }}
                                onChangeExperience={(newvalue) => {
                                  survey.investmentexperience_stock_funds =
                                    newvalue;
                                  setSurvey(survey);
                                  setSurveyUpdate(!survey_update);
                                }}
                              />
                              <ProductRow
                                defaultValueKnowledge={
                                  survey.investmentknowledge_fund_of_funds
                                }
                            defaultValueExperience={
                              survey.investmentexperience_fund_of_funds
                            }
                            question="Dachfonds / Gemischte Fonds"
                            onChangeKnowledge={(newvalue) => {
                              survey.investmentknowledge_fund_of_funds =
                                newvalue;
                              setSurvey(survey);
                              setSurveyUpdate(!survey_update);
                            }}
                                onChangeExperience={(newvalue) => {
                                  survey.investmentexperience_fund_of_funds =
                                    newvalue;
                                  setSurvey(survey);
                                  setSurveyUpdate(!survey_update);
                                }}
                              />
                          </div>
                      </Card>
                    </div>

                    {/* SECTION 2: SERVICES */}
                    <div className="flex flex-col gap-4">
                      <Text h4 style={{ margin: 0 }}>
                        Erfahrungen mit Wertpapierdienstleistungen
                      </Text>
                      <Card style={surveyTableCardStyle}>
                        <div className="flex flex-col divide-y divide-gray-100">
                            <ServiceRow
                              defaultValue={
                                survey.serviceexperience_investment_consultancy
                              }
                              question="Wie viel Erfahrung haben Sie mit der Investition in Wertpapiere über eine Anlageberatung?"
                              onChange={(newvalue) => {
                                survey.serviceexperience_investment_consultancy =
                                  newvalue;
                                setSurvey(survey);
                                setSurveyUpdate(!survey_update);
                              }}
                            />
                            <ServiceRow
                              defaultValue={
                                survey.serviceexperience_investment_brokerage
                              }
                              question="Wie viel Erfahrung haben Sie mit der Investition in Wertpapiere über eine Anlagevermittlung?"
                              onChange={(newvalue) => {
                                survey.serviceexperience_investment_brokerage =
                                  newvalue;
                                setSurvey(survey);
                                setSurveyUpdate(!survey_update);
                              }}
                            />
                            <ServiceRow
                              defaultValue={
                                survey.serviceexperience_investment_management
                              }
                              question="Wie viel Erfahrung haben Sie mit der Investition in Wertpapiere über eine Vermögensverwaltung?"
                              onChange={(newvalue) => {
                                survey.serviceexperience_investment_management =
                                  newvalue;
                                setSurvey(survey);
                                setSurveyUpdate(!survey_update);
                              }}
                            />
                            <ServiceRow
                              defaultValue={survey.serviceexperience_independent}
                              question="Wie viel Erfahrung haben Sie mit der eigenständigen, beratungsfreien Investition in Wertpapiere?"
                              onChange={(newvalue) => {
                                survey.serviceexperience_independent = newvalue;
                                setSurvey(survey);
                                setSurveyUpdate(!survey_update);
                              }}
                            />
                        </div>
                      </Card>
                    </div>

                    {/* SECTION 3: TRANSACTIONS */}
                    <div className="flex flex-col gap-4">
                      <Text h4 style={{ margin: 0 }}>
                        Häufigkeit und Volumen Ihrer Transaktionen
                      </Text>
                      <Card style={surveyTableCardStyle}>
                         <div className="flex flex-col divide-y divide-gray-100">
                           <div className="flex flex-col md:flex-row md:items-center justify-between p-6 gap-4 group hover:bg-gray-50 transition-colors">
                                <div className="md:w-3/5">
                                    <Text b style={{ margin: 0, fontSize: "15px" }}>
                                        Wie viele Investitionen tätigen Sie pro Jahr?
                                    </Text>
                                </div>
                                <div className="md:w-2/5 md:min-w-[200px]">
                                    <CustomSelect
                                      value={survey.avgyearlytransactions?.toString()}
                                      onChange={(val) => {
                                        survey.avgyearlytransactions = val as INVESTMENT_COUNT;
                                        setSurvey(survey);
                                        setSurveyUpdate(!survey_update);
                                      }}
                                      placeholder="Bitte wählen"
                                      options={[
                                         { value: INVESTMENT_COUNT.NO_TRANS, label: "Keine Investitionen" },
                                         { value: INVESTMENT_COUNT.LOWER_5_TRANS, label: "Bis zu 5 Investitionen" },
                                         { value: INVESTMENT_COUNT.LOWER_10_TRANS, label: "Bis zu 10 Investitionen" },
                                         { value: INVESTMENT_COUNT.MORE_THAN_10_TRANS, label: "Über 10 Investitionen" }
                                      ]}
                                    />
                                </div>
                           </div>

                           <div className="flex flex-col md:flex-row md:items-center justify-between p-6 gap-4 group hover:bg-gray-50 transition-colors">
                                <div className="md:w-3/5">
                                    <Text b style={{ margin: 0, fontSize: "15px" }}>
                                        Wie hoch ist der durchschnittliche Gegenwert der
                                        Transaktionen aus der vorherigen Frage?
                                    </Text>
                                </div>
                                <div className="md:w-2/5 md:min-w-[200px]">
                                    <CustomSelect
                                      value={survey.avgtransactionvalue?.toString()}
                                      onChange={(val) => {
                                        survey.avgtransactionvalue = val as INVESTMENT_SIZE;
                                        setSurvey(survey);
                                        setSurveyUpdate(!survey_update);
                                      }}
                                      placeholder="Bitte wählen"
                                      options={[
                                        { value: INVESTMENT_SIZE.LOWER_3000, label: "Bis 3.000€" },
                                        { value: INVESTMENT_SIZE.BETWEEN_3000_AND_5000, label: "Zwischen 3.000€ und 5.000€" },
                                        { value: INVESTMENT_SIZE.BETWEEN_5000_AND_10000, label: "Zwischen 5.000€ und 10.000€" },
                                        { value: INVESTMENT_SIZE.OVER_10000, label: "Mehr als 10.000€" }
                                      ]}
                                    />
                                </div>
                           </div>
                         </div>
                      </Card>
                    </div>

                    <div>
                      <MultistepFormButtonsV2
                        nextButtonText="Bestätigen"
                        disabled={false}
                        back={true}
                        backFunction={back}
                      />
                    </div>

                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const SurveyDone = () => {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          investment_context.nextStep();
        }}
        className="grid gap-8"
      >
        <Text h3>
          Bitte tragen Sie hier Ihre Kenntnisse und Erfahrungen ein.
        </Text>
        <Card style={sectionCardStyle}>
           <div className="flex flex-col gap-4">
               <div>
                  <Text h5 style={{ margin: 0, marginBottom: "8px" }}>Angaben überarbeiten</Text>
                  <Text p style={{ fontSize: "14px", color: "#666", margin: 0 }}>
                    Sie haben für dieses Investment bereits Angaben gemacht. Möchten Sie diese überarbeiten?
                  </Text>
               </div>
               <div className="flex gap-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="px-8 py-4 rounded-full border border-black bg-white text-black font-bold text-sm uppercase tracking-tight transition-all hover:bg-gray-50"
                    onClick={() => setSurveyDone(false)}
                    type="button"
                  >
                    Jetzt überarbeiten
                  </motion.button>
               </div>
           </div>
        </Card>
        <MultistepFormButtonsV2
          back={true}
          backFunction={() => investment_context.prevStep()}
          disabled={false}
        />
      </form>
    );
  };

    return (
      <div className="w-full max-w-4xl min-h-screen flex flex-col gap-8">
        <div className="flex flex-col gap-4 mb-4">
          <Text h3 style={{ margin: 0, fontSize: 'clamp(20px, 5vw, 24px)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '-0.02em' }}>
            Kenntnisse & Erfahrungen
          </Text>
          <Text p type="secondary" style={{ margin: 0, fontSize: 'clamp(14px, 4vw, 16px)', lineHeight: 1.5 }}>
            Um Ihnen ein bestmögliches Anlageerlebnis zu bieten, benötigen wir einige Informationen zu Ihren Vorkenntnissen.
          </Text>
        </div>

        {survey ? (
          <>
            {loading_next_step ? (
              <LoadingScreen className="w-full h-screen" />
            ) : investment_context.last_investment != null &&
              investment_context.investment?.survey == null &&
              !last_survey_done ? (
              <UseLastInvestmentDialogue />
            ) : survey_done ? (
              <SurveyDone />
            ) : active_step === 0 ? (
              <Survey />
            ) : active_step === 1 ? (
              <NoAnswerWaiver />
            ) : active_step === 2 ? (
              <UnsuitabilityWaiver />
            ) : null}
          </>
        ) : null}
      </div>
    );
}

function ProductRow({
  defaultValueKnowledge,
  defaultValueExperience,
  question,
  onChangeKnowledge,
  onChangeExperience,
}: ProductRowProps) {
  const defaultValueKnowledgeBool =
    defaultValueKnowledge === KNOWLEDGE.KNOWLEDGE;

  const returnExperience = (checked: boolean) =>
    checked ? KNOWLEDGE.KNOWLEDGE : KNOWLEDGE.NO_KNOWLEDGE;

  return (
    <div className="flex flex-col md:flex-row md:items-center p-6 gap-4 group hover:bg-gray-50 transition-colors">
        {/* Question / Product Name */}
        <div className="md:w-1/3">
           <Text b style={{ fontSize: "15px" }}>{question}</Text>
        </div>

        {/* Knowledge Toggle */}
        <div className="md:w-1/3 flex items-center gap-3">
             <div className="md:hidden w-1/3 min-w-[100px]">
                <Text small type="secondary" style={{ textTransform: "uppercase", fontSize: "10px", fontWeight: 700, letterSpacing: '0.05em' }}>Kenntnisse</Text>
             </div>
             {/* Added h-8 (32px) which is approx the height of scale=1.6 toggle to ensure proper containment */}
             <div className="flex items-center gap-3">
                <Toggle
                  initialChecked={defaultValueKnowledgeBool}
                  scale={1.6}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    onChangeKnowledge(returnExperience(checked));
                  }}
                />
                <span style={{ fontSize: "13px", fontWeight: 500, color: defaultValueKnowledgeBool ? "#000" : "#888", display: 'inline-block', transform: 'translateY(4px)' }}>
                  {defaultValueKnowledgeBool ? "JA" : "NEIN"}
                </span>
             </div>
        </div>

        {/* Experience Select */}
        <div className="md:w-1/3 flex flex-col md:block">
            <div className="md:hidden mb-1">
                 <Text small type="secondary" style={{ textTransform: "uppercase", fontSize: "10px", fontWeight: 700, letterSpacing: '0.05em' }}>Erfahrungen</Text>
            </div>
            <CustomSelect
                width="100%"
                value={defaultValueExperience?.toString()}
                onChange={(val) => {
                   onChangeExperience(val as EXPERIENCE);
                }}
                placeholder="Bitte wählen"
                options={[
                   { value: EXPERIENCE.NO_ANSWER, label: "Keine" },
                   { value: EXPERIENCE.ZERO_TO_THREE_YEARS, label: "< 3 Jahre" },
                   { value: EXPERIENCE.THREE_TO_FIVE_YEARS, label: "3 - 5 Jahre" },
                   { value: EXPERIENCE.GREATER_THAN_FIVE_YEARS, label: "> 5 Jahre" }
                ]}
            />
        </div>
    </div>
  );
}

function ServiceRow({
  defaultValue,
  question,
  onChange,
}: ServiceRowProps) {

  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between p-6 gap-4 group hover:bg-gray-50 transition-colors">
        <div className="md:w-3/5">
            <Text b style={{ margin: 0, fontSize: "15px" }}>
                {question}
            </Text>
        </div>
        <div className="md:w-2/5 md:min-w-[200px]">
             <CustomSelect
                width="100%"
                value={defaultValue?.toString()}
                onChange={(val) => {
                onChange(val as SERVICE_KNOWLEDGE_OR_EXPERIENCE);
                }}
                placeholder="Bitte wählen"
                options={[
                    { value: SERVICE_KNOWLEDGE_OR_EXPERIENCE.NO_KNOWLEDGE_OR_EXPERIENCE, label: "Keine Erfahrung" },
                    { value: SERVICE_KNOWLEDGE_OR_EXPERIENCE.ZERO_TO_THREE_YEARS, label: "Bis 3 Jahre Erfahrung" },
                    { value: SERVICE_KNOWLEDGE_OR_EXPERIENCE.THREE_TO_FIVE_YEARS, label: "Zwischen 3 und 5 Jahre Erfahrung" },
                    { value: SERVICE_KNOWLEDGE_OR_EXPERIENCE.GREATER_THAN_FIVE_YEARS, label: "Über 5 Jahre Erfahrung" }
                ]}
            />
        </div>
    </div>
  );
}

type ProductRowProps = {
  defaultValueKnowledge: KNOWLEDGE;
  defaultValueExperience: EXPERIENCE;
  question: string;
  onChangeExperience: (value: EXPERIENCE) => void;
  onChangeKnowledge: (value: KNOWLEDGE) => void;
};

type ServiceRowProps = {
  defaultValue: SERVICE_KNOWLEDGE_OR_EXPERIENCE;
  question: string;
  onChange: (value: SERVICE_KNOWLEDGE_OR_EXPERIENCE) => void;
};
